// The end of a bout: its sumo name written into the result line, the verdict put on the
// article for the stage (the fall, the bow), and the one telemetry event.
// Nothing moves here: the ending is played on the stage (static/js/clouds.js).
import { track } from "./telemetry.ts";
import { $, markEchoes, parseEchoAttr, reduced } from "./dom.ts";
import { initOverlay } from "./overlay.ts";

/** the stage never said the bout was decided (no stage after all, a hidden tab): the result line shows by then anyway, ms */
const DECIDED_FALLBACK = 4000;

type Kimarite = [kanji: string, romaji: string, english: string];

/** the same table as pages/mod.rs `kimarite` */
const KIMARITE: Record<string, Kimarite> = {
  yield: ["参った", "maitta", "gave up"],
  overlength: ["押し出し", "oshidashi", "pushed out"],
  incoherent: ["勇み足", "isamiashi", "stepped out"],
  repetition: ["叩き込み", "hatakikomi", "slapped down"],
  refusal: ["不戦勝", "fusenshō", "win by default"],
  draw: ["引き分け", "hikiwake", "draw"],
  cap: ["取り直し", "torinaoshi", "rematch"],
  error: ["無勝負", "mushōbu", "no bout"],
  forfeit: ["棄権", "kiken", "forfeit"],
};

/** The sumo name of an ending. */
export function nameOf(result: Outcome, reason: Reason): Kimarite {
  if (result === "nocontest") return reason === "cap" ? KIMARITE.cap : KIMARITE.error;
  if (result === "draw") return KIMARITE.draw;
  if (reason === "error") return KIMARITE.forfeit; // a side's provider could not answer: it loses
  return KIMARITE[reason] || KIMARITE.draw;
}

export const toneOf = (result: Outcome): "red" | "gold" | "grey" =>
  result === "nocontest" ? "grey" : result === "draw" ? "gold" : "red";

/**
 * Seal a bout on the page, once: `data-verdict` on the article (what the stage reads), the result
 * line as the server renders it for a finished bout, the loop's echoes marked when a side lost to one.
 */
export function sealBout(bout: HTMLElement | null, d: DoneEvent) {
  if (!bout || bout.dataset.verdict) return;
  bout.dataset.verdict = d.result;
  bout.dataset.reason = d.reason;
  track("bout_complete", { kimarite_reason: d.reason, winner_side: d.result });
  const [kanji, romaji, english] = nameOf(d.result, d.reason);
  const result = $("[data-result]", bout);
  // on the stage the pair fight on a moment after the verdict (fx/fight.ts SEAL.hold): the result line and the overlay's
  // verdict wait for the stage's basho:decided, so the name of the ending comes with the fall and not before it
  const show = () => {
    if (result) result.hidden = false;
    initOverlay();
  };
  if (bout.classList.contains("staged") && !reduced) {
    let shown = false;
    const once = () => {
      if (shown) return;
      shown = true;
      show();
    };
    bout.addEventListener("basho:decided", once, { once: true });
    setTimeout(once, DECIDED_FALLBACK);
  } else show();
  if (result) {
    if (!result.querySelector(".seal"))
      result.innerHTML = `<span class="seal small ${toneOf(d.result)}" lang="ja" title="${romaji} · ${english}">${kanji}</span><span class="kimarite-name">${romaji}</span>bout over: ${d.result} · ${d.reason} · turn ${d.turns}`;
  }
  // a loss to a loop: the final message is the loop, and the echoed phrase is marked wherever it recurs
  if (d.reason === "repetition" || d.reason === "incoherent") {
    const list = $("[data-transcript]", bout),
      msgs = list ? [...list.querySelectorAll("li.msg:not(.thinking)")] : [];
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg) {
      lastMsg.classList.add("looped");
      if (d.reason === "repetition") lastMsg.classList.add("fatal");
    }
    for (const li of msgs) {
      const body = $(".body", li);
      if (!body) continue;
      const ranges = d.echoes?.[(li as HTMLElement).dataset.turn || ""] ?? parseEchoAttr(body.dataset.echo);
      if (!ranges.length) continue;
      li.classList.add("echoed");
      markEchoes(body, ranges);
    }
  }
}
